import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import CalenderIcon from "../../../../Assets/Images/calendar-icon.svg";
import RightIcon from "../../../../Assets/Images/right-cal.svg";
import LeftIcon from "../../../../Assets/Images/left-cal.svg";
import "./Calendar.css";

function RangeCalendar({
  value = {},
  min,
  max,
  onChange,
  showSelector,
  mode = "range",
}) {
  const [openCalendar, setOpenCalendar] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const portalRef = useRef(null);

  useEffect(() => {
    if (showSelector == true) {
      setOpenCalendar(true);
    }
  }, [showSelector]);

  // Helper function to parse date string in DD/MM/YYYY format
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    if (dateStr instanceof Date) return dateStr;
    
    // Handle DD/MM/YYYY format
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
      const year = parseInt(parts[2], 10);
      return new Date(year, month, day);
    }
    
    return new Date(dateStr);
  };

  // Helper function to normalize date to midnight for comparison
  const normalizeDate = (date) => {
    if (!date) return null;
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  // Helper function to check if two dates are the same day
  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    const d1 = normalizeDate(date1);
    const d2 = normalizeDate(date2);
    return d1.getTime() === d2.getTime();
  };

  const [range, setRange] = useState({
    start: parseDate(value.start) || null,
    end: parseDate(value.end) || null,
  });

  const [currentMonth, setCurrentMonth] = useState(
    parseDate(value.start) || new Date()
  );
  const calendarRef = useRef();

  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = Array.from({ length: 12 }, (_, i) =>
    new Date(0, i).toLocaleString("default", { month: "long" })
  );
  const years = Array.from({ length: 50 }, (_, i) => 1980 + i);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      const inTrigger = triggerRef.current && triggerRef.current.contains(e.target);
      const inPortal = portalRef.current && portalRef.current.contains(e.target);
      const inWrapper = calendarRef.current && calendarRef.current.contains(e.target);
      if (!inTrigger && !inPortal && !inWrapper) {
        setOpenCalendar(false);
      }
    };
    if (openCalendar)
      document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [openCalendar]);

  const updateCoords = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
  };

  useEffect(() => {
    if (!openCalendar) return;
    updateCoords();
    const closeOnScroll = (e) => {
      if (portalRef.current && portalRef.current.contains(e.target)) return;
      setOpenCalendar(false);
    };
    window.addEventListener("scroll", closeOnScroll, true);
    window.addEventListener("resize", closeOnScroll);
    return () => {
      window.removeEventListener("scroll", closeOnScroll, true);
      window.removeEventListener("resize", closeOnScroll);
    };
  }, [openCalendar]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const generateCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = new Date(year, month, 1).getDay();

    const calendar = [];

    for (let i = 0; i < firstDay; i++) calendar.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      calendar.push(new Date(year, month, d));
    }

    return calendar;
  };

  const handleDateSelect = (date) => {
    let newRange;

    if (mode === "single") {
      newRange = { start: date, end: null };
      setRange(newRange);

      onChange?.({
        start: date.toLocaleDateString("en-GB"),
        end: null,
      });

      return;
    }

    if (!range.start || range.end) {
      newRange = { start: date, end: null };
    } else if (date >= range.start) {
      newRange = { start: range.start, end: date };
    } else {
      newRange = { start: date, end: null };
    }

    setRange(newRange);

    onChange?.({
      start: newRange.start.toLocaleDateString("en-GB"),
      end: newRange.end?.toLocaleDateString("en-GB") || null,
    });
  };

  const isInRange = (date) => {
    if (!range.start || !range.end || !date) return false;
    const normalized = normalizeDate(date);
    const start = normalizeDate(range.start);
    const end = normalizeDate(range.end);
    return normalized >= start && normalized <= end;
  };

  const handleMonthChange = (e) => {
    const newMonth = parseInt(e.target.value);
    setCurrentMonth(new Date(currentMonth.getFullYear(), newMonth, 1));
  };

  const handleYearChange = (e) => {
    const newYear = parseInt(e.target.value);
    setCurrentMonth(new Date(newYear, currentMonth.getMonth(), 1));
  };

  const goToPreviousMonth = () => {
    const prevMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() - 1,
      1
    );
    if (!min || prevMonth >= new Date(min)) {
      setCurrentMonth(prevMonth);
    }
  };

  const goToNextMonth = () => {
    const nextMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + 1,
      1
    );
    if (!max || nextMonth <= new Date(max)) {
      setCurrentMonth(nextMonth);
    }
  };

  const formatRange = () => {
    if (!range.start) return "Select Date";
    if (range.start && !range.end)
      return `${range.start.toLocaleDateString("en-GB")} - ...`;
    return `${range.start.toLocaleDateString(
      "en-GB"
    )} - ${range.end.toLocaleDateString("en-GB")}`;
  };

  return (
    <div className="calendar-wrapper" ref={calendarRef}>
      {showSelector == true && (
        <div
          className="custom-calendar-input"
          ref={triggerRef}
          onClick={() => {
            if (!openCalendar) updateCoords();
            setOpenCalendar(!openCalendar);
          }}
        >
          <div className="calender-text value">{formatRange()}</div>
          <img src={CalenderIcon} alt="Calendar Icon" />
        </div>
      )}

      {showSelector == false ? (
        <>
          <div className="calendar-header">
            <img
              src={LeftIcon}
              alt="Previous"
              onClick={goToPreviousMonth}
              className="month-nav-icon"
            />
            <select
              onChange={handleMonthChange}
              value={currentMonth.getMonth()}
            >
              {months.map((m, i) => (
                <option key={i} value={i}>
                  {m}
                </option>
              ))}
            </select>
            <select
              onChange={handleYearChange}
              value={currentMonth.getFullYear()}
            >
              {years.map((y, i) => (
                <option key={i} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <img
              src={RightIcon}
              alt="Next"
              onClick={goToNextMonth}
              className="month-nav-icon"
            />
          </div>

          <div className="calendar-grid">
            {days.map((day) => (
              <div className="calendar-day calendar-head" key={day}>
                {day}
              </div>
            ))}
          </div>

          <div className="border-row"></div>

          <div className="calendar-grid">
            {generateCalendar().map((date, index) => {
              if (!date) {
                return (
                  <div key={index} className="calendar-day calendar-empty">
                    
                  </div>
                );
              }

              const isDisabled =
                (min && normalizeDate(date) < normalizeDate(new Date(min))) ||
                (max && normalizeDate(date) > normalizeDate(new Date(max)));

              const isSelected =
                isSameDay(date, range.start) ||
                (mode === "range" && isSameDay(date, range.end));

              return (
                <div
                  key={index}
                  className={`calendar-day calendar-active
                    ${isDisabled ? "disabled" : ""} 
                    ${isSelected ? "selected" : ""} 
                    ${!isSelected && isInRange(date) ? "in-range" : ""}`}
                  onClick={() => !isDisabled && handleDateSelect(date)}
                >
                  {date.getDate()}
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <>
          {openCalendar && createPortal(
            <div
              ref={portalRef}
              className="custom-calendar-popup"
              style={{ position: "fixed", top: coords.top, left: coords.left, zIndex: 9999 }}
            >
              <div className="calendar-header">
                <img
                  src={LeftIcon}
                  alt="Previous"
                  onClick={goToPreviousMonth}
                  className="month-nav-icon"
                />
                <select
                  onChange={handleMonthChange}
                  value={currentMonth.getMonth()}
                >
                  {months.map((m, i) => (
                    <option key={i} value={i}>
                      {m}
                    </option>
                  ))}
                </select>
                <select
                  onChange={handleYearChange}
                  value={currentMonth.getFullYear()}
                >
                  {years.map((y, i) => (
                    <option key={i} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
                <img
                  src={RightIcon}
                  alt="Next"
                  onClick={goToNextMonth}
                  className="month-nav-icon"
                />
              </div>

              <div className="calendar-grid">
                {days.map((day) => (
                  <div className="calendar-day calendar-head" key={day}>
                    {day}
                  </div>
                ))}
              </div>

              <div className="border-row"></div>

              <div className="calendar-grid">
                {generateCalendar().map((date, index) => {
                  if (!date) {
                    return (
                      <div key={index} className="calendar-day calendar-empty">

                      </div>
                    );
                  }

                  const isDisabled =
                    (min && normalizeDate(date) < normalizeDate(new Date(min))) ||
                    (max && normalizeDate(date) > normalizeDate(new Date(max)));

                  const isSelected =
                    isSameDay(date, range.start) ||
                    (mode === "range" && isSameDay(date, range.end));

                  return (
                    <div
                      key={index}
                      className={`calendar-day calendar-active
                        ${isDisabled ? "disabled" : ""}
                        ${isSelected ? "selected" : ""}
                        ${!isSelected && isInRange(date) ? "in-range" : ""}`}
                      onClick={() => !isDisabled && handleDateSelect(date)}
                    >
                      {date.getDate()}
                    </div>
                  );
                })}
              </div>
            </div>,
            document.body
          )}
        </>
      )}
    </div>
  );
}

export default RangeCalendar;